
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useRouter } from '@/lib/router-compat';
import { useQueryClient } from '@tanstack/react-query';
import { authApi, organizationApi } from '@/lib/api/auth';
import { apiClient } from '@/lib/api/client';
import { storage } from '@/lib/storage';
import { registerPush, unregisterPush } from '@/lib/native/push';
import { purgePersistedCache } from '@/lib/queryPersister';
import type { LoginRequest, RegisterRequest, Organization } from '@/types/auth';

interface AuthContextType {
  token: string | null;
  userId: string | null;
  organizations: Organization[];
  currentOrg: Organization | null;
  isLoading: boolean;
  isSuperAdmin: boolean;
  pendingOnboarding: boolean;
  setPendingOnboarding: (value: boolean) => void;
  login: (data: LoginRequest) => Promise<void>;
  register: (data: RegisterRequest) => Promise<any>;
  logout: () => void;
  switchOrganization: (orgId: string) => Promise<void>;
  loadOrganizations: () => Promise<Organization[]>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Persisted key for the FCM registration token. Stored so logout can explicitly DELETE the
// backend row via /api/devices/{token} instead of waiting for FCM send failure to garbage-collect.
const FCM_TOKEN_STORAGE_KEY = 'fcm_token';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [token, setToken] = useState<string | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [currentOrg, setCurrentOrg] = useState<Organization | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [pendingOnboarding, setPendingOnboarding] = useState(false);
  const router = useRouter();
  const queryClient = useQueryClient();
  const switchCounterRef = useRef(0);
  const pushRegisteredRef = useRef(false);

  // Push registration helper — fire-and-forget, idempotent via `registered` flag inside push.ts.
  // Stores the received FCM token in storage so logout can explicitly unregister the device row.
  const ensurePushRegistered = useCallback(() => {
    if (pushRegisteredRef.current) return;
    pushRegisteredRef.current = true;
    registerPush({
      onTap: (route) => {
        // Client-side nav — no hard reload, preserves React Query cache.
        router.push(route);
      },
      onForeground: () => {
        // Foreground handler intentionally empty; a future step wires this to a toast/badge.
      },
      onTokenReceived: async (fcmToken) => {
        await storage.set(FCM_TOKEN_STORAGE_KEY, fcmToken);
      },
    }).catch((err) => console.error('[auth] registerPush failed', err));
  }, [router]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Native: prime Preferences cache before any read. Web: no-op.
      await storage.hydrate();
      if (cancelled) return;

      const storedToken = storage.getSync('token');
      const storedOrg = storage.getSync('currentOrg');

      if (storedToken) {
        setToken(storedToken);
        try {
          const payload = JSON.parse(atob(storedToken.split('.')[1]));
          setUserId(payload.sub ?? null);
          setIsSuperAdmin(payload.superAdmin === 'true');
        } catch {
          setUserId(null);
          setIsSuperAdmin(false);
        }
        if (storedOrg) {
          try {
            setCurrentOrg(JSON.parse(storedOrg));
            // Token + orgId already present → session restored; register push immediately.
            ensurePushRegistered();
          } catch {
            // Corrupt org payload — ignore, user will switch on next render.
          }
        }
      }
      setIsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [ensurePushRegistered]);

  const login = useCallback(async (data: LoginRequest) => {
    const response = await authApi.login(data);

    // Save token immediately so subsequent API calls can use it
    await storage.set('token', response.token);
    setToken(response.token);
    setUserId(response.userId);
    setIsSuperAdmin(response.isSuperAdmin);

    try {
      // Auto-load organizations and select default if available
      const orgs = await organizationApi.list();
      setOrganizations(orgs);

      if (response.defaultOrganizationId) {
        // Find the default org and switch to it
        const defaultOrg = orgs.find(o => o.id === response.defaultOrganizationId);
        if (defaultOrg) {
          const switchResponse = await authApi.switchOrg(defaultOrg.id);
          await storage.set('token', switchResponse.token);
          setToken(switchResponse.token);

          const org: Organization = {
            id: switchResponse.orgId,
            name: switchResponse.orgName,
            role: switchResponse.role,
          };
          await storage.set('currentOrg', JSON.stringify(org));
          setCurrentOrg(org);

          // Only register push once we have a tenant-scoped token — /api/devices/register
          // requires the RequireOrg policy.
          ensurePushRegistered();
        }
      }

      router.push('/dashboard');
    } catch (error) {
      // Cleanup on failure - remove token and rethrow
      await storage.remove('token');
      setToken(null);
      throw error;
    }
  }, [router, ensurePushRegistered]);

  const register = useCallback(async (data: RegisterRequest) => {
    const response = await authApi.register(data);
    const loginResponse = await authApi.login(data);
    // Clear previous session's org so the new account doesn't inherit it
    await storage.remove('currentOrg');
    await storage.set('token', loginResponse.token);
    setToken(loginResponse.token);
    setUserId(loginResponse.userId);
    setCurrentOrg(null);
    setOrganizations([]);
    return response;
  }, []);

  const logout = useCallback(() => {
    // Async side-effects run in background; UI cleanup is synchronous so the login redirect
    // is instant and doesn't flash stale data.
    (async () => {
      const fcmToken = storage.getSync(FCM_TOKEN_STORAGE_KEY);
      if (fcmToken) {
        try {
          // Best-effort — the device row is auto-cleaned on next failed FCM delivery anyway.
          await apiClient.delete(`/api/devices/${encodeURIComponent(fcmToken)}`);
        } catch (err) {
          console.warn('[auth] device unregister failed', err);
        }
        await storage.remove(FCM_TOKEN_STORAGE_KEY);
      }
      await unregisterPush();
      await storage.remove('token');
      await storage.remove('currentOrg');
    })();

    pushRegisteredRef.current = false;
    setToken(null);
    setUserId(null);
    setCurrentOrg(null);
    setOrganizations([]);
    setIsSuperAdmin(false);
    queryClient.removeQueries();
    // Wipe persisted RQ cache so the next user doesn't see prior session's tenant data.
    void purgePersistedCache();
    router.push('/login');
  }, [router, queryClient]);

  const switchOrganization = useCallback(async (orgId: string) => {
    const response = await authApi.switchOrg(orgId);

    // Build org from switch response so token and currentOrg update
    // in the same synchronous block. This prevents the SyncProvider from
    // seeing an intermediate state (new token + old orgId) which causes
    // a SignalR connection to start and immediately get torn down.
    const org: Organization = {
      id: response.orgId,
      name: response.orgName,
      role: response.role,
    };

    await storage.set('token', response.token);
    await storage.set('currentOrg', JSON.stringify(org));
    setToken(response.token);
    setCurrentOrg(org);

    // Clear React Query cache — old org's data is no longer valid
    queryClient.removeQueries();
    // Persisted IndexedDB cache must follow suit; otherwise next reload rehydrates the previous org.
    void purgePersistedCache();

    // Now that we have a tenant-scoped token, (re)ensure push is registered. Idempotent.
    ensurePushRegistered();

    // Refresh org list in background to get complete data (isDefault, description)
    const switchId = ++switchCounterRef.current;
    organizationApi.list().then(async orgs => {
      // Discard stale response if another switch happened since this one
      if (switchCounterRef.current !== switchId) return;
      setOrganizations(orgs);
      const freshOrg = orgs.find(o => o.id === response.orgId);
      if (freshOrg) {
        await storage.set('currentOrg', JSON.stringify(freshOrg));
        setCurrentOrg(freshOrg);
      }
    });

    router.refresh();
  }, [router, queryClient, ensurePushRegistered]);

  const loadOrganizations = useCallback(async () => {
    const orgs = await organizationApi.list();
    setOrganizations(orgs);

    const storedOrg = storage.getSync('currentOrg');
    if (storedOrg) {
      const parsed = JSON.parse(storedOrg);
      const fresh = orgs.find(o => o.id === parsed.id);
      if (fresh) {
        await storage.set('currentOrg', JSON.stringify(fresh));
        setCurrentOrg(fresh);
      }
    } else {
      const defaultOrg = orgs.find(o => o.isDefault);
      if (defaultOrg) await switchOrganization(defaultOrg.id);
    }

    return orgs;
  }, [switchOrganization]);

  const value = useMemo(() => ({
    token,
    userId,
    organizations,
    currentOrg,
    isLoading,
    isSuperAdmin,
    pendingOnboarding,
    setPendingOnboarding,
    login,
    register,
    logout,
    switchOrganization,
    loadOrganizations,
  }), [token, userId, organizations, currentOrg, isLoading, isSuperAdmin, pendingOnboarding, login, register, logout, switchOrganization, loadOrganizations]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
